/**
 * CR337 + CR305: Ribbon Color Section
 * 
 * Per-deal-type color controls for the corner ribbon.
 * Supports three color sources:
 * - custom_hex: Admin picks a HEX color
 * - theme_token: Uses deal-type theme default (Theme Labs SSOT)
 * - asset_default: Uses default_tint from Asset Studio asset
 * 
 * CR305: Theme Labs dealTypeThemes is the SSOT for "Theme Default".
 * Hardcoded DEAL_TYPE_DEFAULT_HEX is only the last-resort fallback.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Palette, Info, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { normalizeHex, isValidHex } from "@/lib/deals/hexColorUtils";
import {
  resolveRibbonColor,
  getResolvedRibbonHex,
  DEAL_TYPE_DEFAULT_HEX,
  type RibbonColorSource,
  type RibbonColorConfig,
} from "@/lib/deal-cards/resolveRibbonColor";
import type { DealCategory } from "@/lib/deals/dealTypeRegistry";
import type { DealTypeThemeColors } from "@/lib/deals/globalThemeTokens";

// ================================================================
// TYPES
// ================================================================

interface RibbonColorSectionProps {
  /** Current color config */
  colorConfig: RibbonColorConfig;
  /** Deal type for theme defaults */
  dealTypeKey: string;
  /** Asset default_tint from selected ribbon asset (if any) */
  assetDefaultTint?: string | null;
  /** Whether a ribbon asset is selected */
  hasAsset: boolean;
  /** Callback when config changes */
  onChange: (config: RibbonColorConfig) => void;
  /** CR305: Theme Labs per-deal-type themes (SSOT for "Theme Default") */
  dealTypeThemes?: Record<string, DealTypeThemeColors> | null;
  /** CR306: Optional styling.ribbonBaseHex for fallback precedence */
  stylingFallbackHex?: string | null;
}

// Debounce delay
const DEBOUNCE_MS = 200;

// ================================================================
// COMPONENT
// ================================================================

export function RibbonColorSection({
  colorConfig,
  dealTypeKey,
  assetDefaultTint,
  hasAsset,
  onChange,
  dealTypeThemes,
  stylingFallbackHex,
}: RibbonColorSectionProps) {
  const dealCategory = dealTypeKey as DealCategory;
  const [localHex, setLocalHex] = useState(colorConfig.color_hex ?? "");
  const [hexError, setHexError] = useState<string | null>(null);

  // Sync external config to local state
  useEffect(() => {
    setLocalHex(colorConfig.color_hex ?? "");
  }, [colorConfig.color_hex]);

  // CR305/CR306: Resolve using Theme Labs SSOT + styling fallback
  const resolved = useMemo(
    () => resolveRibbonColor(colorConfig, dealCategory, assetDefaultTint, dealTypeThemes, stylingFallbackHex),
    [colorConfig, dealCategory, assetDefaultTint, dealTypeThemes, stylingFallbackHex]
  );

  const effectiveHex = useMemo(
    () => getResolvedRibbonHex(colorConfig, dealCategory, assetDefaultTint, dealTypeThemes, stylingFallbackHex),
    [colorConfig, dealCategory, assetDefaultTint, dealTypeThemes, stylingFallbackHex]
  );

  // Debounced commit for hex input
  useEffect(() => {
    if (colorConfig.color_source !== "custom_hex") return;
    const timer = setTimeout(() => {
      if (!localHex) {
        setHexError(null);
        return;
      }
      const normalized = normalizeHex(localHex);
      if (!normalized) {
        setHexError("Invalid HEX format. Use #RGB or #RRGGBB");
        return;
      }
      setHexError(null);
      if (normalized !== colorConfig.color_hex) {
        onChange({ ...colorConfig, color_hex: normalized });
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [localHex, colorConfig, onChange]);

  // Handle source change
  const handleSourceChange = useCallback(
    (source: RibbonColorSource) => {
      onChange({
        color_source: source,
        color_hex: source === "custom_hex" ? (colorConfig.color_hex ?? null) : colorConfig.color_hex,
      });
    },
    [onChange, colorConfig.color_hex]
  );

  // Handle hex input
  const handleHexInput = useCallback((value: string) => {
    setLocalHex(value);
    if (value && !isValidHex(value)) {
      setHexError("Enter valid HEX (e.g., #D3AF37)");
    } else {
      setHexError(null);
    }
  }, []);

  // CR305: Theme default hex — prefer Theme Labs SSOT, fallback to hardcoded legacy
  const themeLabsRibbonHex = dealTypeThemes?.[dealCategory]?.ribbonBaseHex;
  const themeDefaultHex = (themeLabsRibbonHex && normalizeHex(themeLabsRibbonHex))
    || DEAL_TYPE_DEFAULT_HEX[dealCategory]
    || DEAL_TYPE_DEFAULT_HEX.UNCLASSIFIED;
  const themeDefaultSource = themeLabsRibbonHex ? "Theme Labs" : "Legacy default";

  return (
    <div className="space-y-3 p-3 bg-muted/40 rounded-md border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-primary" />
          <Label className="text-xs font-medium">Ribbon Fill Color</Label>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[240px]">
              <p className="text-xs">
                Controls the corner ribbon background color.
                The ribbon asset provides shape only; color is applied separately.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Source Radio Group */}
      <RadioGroup
        value={colorConfig.color_source}
        onValueChange={(v) => handleSourceChange(v as RibbonColorSource)}
        className="space-y-2"
      >
        {/* Custom HEX */}
        <div className="flex items-start gap-2">
          <RadioGroupItem value="custom_hex" id="color-custom" className="mt-0.5" />
          <div className="flex-1 space-y-1">
            <Label htmlFor="color-custom" className="text-xs cursor-pointer font-medium">
              Custom Color
            </Label>
            <p className="text-[10px] text-muted-foreground">Pick a specific HEX color</p>
          </div>
        </div>

        {/* HEX input (only when custom_hex is selected) */}
        {colorConfig.color_source === "custom_hex" && (
          <div className="pl-6 space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input
                  type="text"
                  value={localHex}
                  onChange={(e) => handleHexInput(e.target.value)}
                  placeholder="#059669"
                  className={cn(
                    "h-8 text-xs font-mono pr-10",
                    hexError && "border-destructive focus-visible:ring-destructive"
                  )}
                />
                {/* Native color picker */}
                <input
                  type="color"
                  value={normalizeHex(localHex) || themeDefaultHex}
                  onChange={(e) => {
                    const hex = e.target.value.toUpperCase();
                    setLocalHex(hex);
                    setHexError(null);
                    onChange({ ...colorConfig, color_hex: hex });
                  }}
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 border-0 p-0 cursor-pointer rounded"
                  title="Pick a color"
                />
              </div>
              {/* Swatch preview */}
              <div
                className="w-8 h-8 rounded border-2 border-border shrink-0"
                style={{
                  backgroundColor: normalizeHex(localHex) || "#e0e0e0",
                }}
              />
            </div>
            {hexError && (
              <div className="flex items-center gap-1 text-destructive text-[10px]">
                <AlertCircle className="h-3 w-3" />
                {hexError}
              </div>
            )}
          </div>
        )}

        {/* Theme Token */}
        <div className="flex items-start gap-2">
          <RadioGroupItem value="theme_token" id="color-theme" className="mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Label htmlFor="color-theme" className="text-xs cursor-pointer font-medium">
                Theme Default
              </Label>
              <div
                className="w-4 h-4 rounded-sm border border-border shrink-0"
                style={{ backgroundColor: themeDefaultHex }}
              />
              <span className="text-[10px] text-muted-foreground font-mono">{themeDefaultHex}</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {themeDefaultSource === "Theme Labs"
                ? `From Theme Labs → ${dealTypeKey}`
                : `Legacy default (${dealTypeKey})`}
            </p>
          </div>
        </div>

        {/* Asset Default */}
        <div className="flex items-start gap-2">
          <RadioGroupItem
            value="asset_default"
            id="color-asset"
            className="mt-0.5"
            disabled={!hasAsset}
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Label
                htmlFor="color-asset"
                className={cn(
                  "text-xs cursor-pointer font-medium",
                  !hasAsset && "text-muted-foreground"
                )}
              >
                Asset Studio Tint
              </Label>
              {assetDefaultTint && (
                <>
                  <div
                    className="w-4 h-4 rounded-sm border border-border shrink-0"
                    style={{ backgroundColor: normalizeHex(assetDefaultTint) || "#808080" }}
                  />
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {normalizeHex(assetDefaultTint)}
                  </span>
                </>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {hasAsset
                ? assetDefaultTint
                  ? "Uses default_tint from the selected ribbon asset"
                  : "Selected asset has no default tint set"
                : "Select a ribbon asset first to use this option"}
            </p>
          </div>
        </div>
      </RadioGroup>

      {/* Resolved Color Preview + CR306 Debug Metadata */}
      <div className="pt-2 border-t">
        <Label className="text-[10px] text-muted-foreground mb-1.5 block">Effective Color</Label>
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-md border-2 border-border"
            style={{ backgroundColor: effectiveHex }}
          />
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-mono font-medium">{effectiveHex}</span>
              <Badge variant="outline" className="text-[9px] h-4 px-1">
                {resolved.source === "custom_hex"
                  ? "Custom"
                  : resolved.source === "theme_token"
                    ? "Theme"
                    : resolved.source === "asset_default"
                      ? "Asset"
                      : resolved.source === "styling_fallback"
                        ? "Styling"
                        : "Fallback"}
              </Badge>
            </div>
            <p className="text-[10px] text-muted-foreground">
              {resolved.debugLabel}
              {resolved.source === "theme_token" && themeDefaultSource === "Theme Labs" && " (Theme Labs SSOT)"}
            </p>
            {/* CR306: Debug metadata — resolved path + theme source */}
            <p className="text-[9px] text-muted-foreground/60 font-mono">
              src: {resolved.source} · theme: {themeDefaultSource === "Theme Labs" ? "saved" : "legacy"} · deal: {dealTypeKey}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RibbonColorSection;
